import { createApi } from "@reduxjs/toolkit/query/react";
import RtkBaseQueryWithReauth from "./RtkBaseQueryWithReauth";
import ApiPath from "./ConstantApi";

export interface PrefixModel {
  id?: string;
  documentType: "ORDER" | "INVOICE" | "BANKTRANSFER" | "CREDITMEMO" | "REFUNDINVOICE";
  prefix: string;
  currentNumber: number;
}

export interface BankDetailsChange {
  field: string;
  from?: string;
  to?: string;
}

export interface BankDetailsLog {
  _id: string;
  action: "created" | "updated";
  changes: BankDetailsChange[];
  changedByName?: string;
  ipAddress?: string;
  createdAt: string;
}

export interface BankDetailsLogsResponse {
  status: string;
  totalRecords: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  data: BankDetailsLog[];
}

export interface BankDetailsModel {
  _id?: string;
  bankName: string;
  accountHolderName: string;
  iban: string;
  bic?: string;
  accountNumber?: string;
  sortCode?: string;
  bankAddress?: string;
  notes?: string;
  isActive?: boolean;
}

export const AdminSettingService = createApi({
  reducerPath: "AdminSettingService",
  baseQuery: RtkBaseQueryWithReauth(process.env.REACT_APP_BASE_URL || ""),
  tagTypes: ["Prefixes", "BankDetails"],
  endpoints: (builder) => ({
    //Number Series GET PREFIX LIST
    getPrefixes: builder.query<PrefixModel[], void>({
      query: () => ({
        url: `${ApiPath.getPrefixes}`,
        method: "GET",
      }),
      transformResponse: (response: { data: PrefixModel[] }) => response.data,
      providesTags: ["Prefixes"],
    }),

    //Number Series ADD PREFIX
    addOrUpdatePrefix: builder.mutation<void, PrefixModel>({
      query: (newPrefix) => ({
        url: `${ApiPath.addOrUpdatePrefix}`,
        method: "POST",
        body: {
          documentType: newPrefix.documentType,
          prefix: newPrefix.prefix,
          currentNumber: newPrefix.currentNumber,
        },
      }),
      invalidatesTags: ["Prefixes"],
    }),
    adminChangePassword: builder.mutation<
      any,
      { employeeId: string; newPassword: string }
    >({
      query: (body) => ({
        url: `${ApiPath.adminchangepassword}`,
        method: "POST",
        body,
      }),
    }),

    // Bank Details
    getBankDetails: builder.query<BankDetailsModel | null, void>({
      query: () => ({
        url: `${ApiPath.getActiveBankDetails}`,
        method: "GET",
      }),
      transformResponse: (response: { data: BankDetailsModel | null }) => response.data,
      providesTags: ["BankDetails"],
    }),

    saveBankDetails: builder.mutation<any, Partial<BankDetailsModel>>({
      query: (data) => ({
        url: `${ApiPath.saveBankDetails}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["BankDetails"],
    }),

    // Change history for the bank account configuration.
    getBankDetailsLogs: builder.query<
      BankDetailsLogsResponse,
      { page?: number; pageSize?: number } | void
    >({
      query: (args) => ({
        url: `${ApiPath.bankDetailsLogs}`,
        method: "GET",
        params: { page: args?.page ?? 1, pageSize: args?.pageSize ?? 20 },
      }),
      // Saving new details must refresh the history.
      providesTags: ["BankDetails"],
    }),
  }),
});

export const {
  useGetPrefixesQuery,
  useAddOrUpdatePrefixMutation,
  useAdminChangePasswordMutation,
  useGetBankDetailsQuery,
  useSaveBankDetailsMutation,
  useGetBankDetailsLogsQuery,
} = AdminSettingService;
